import { apiHelpers } from '../utils/api';
import {
  Bill,
  CreateBillData,
  UpdateBillData,
  BillsResponse,
  BillFilters,
  BillStats
} from '../types/bill';

export const billService = {
  async getBills(filters: BillFilters = {}): Promise<BillsResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.vendorId) params.append('vendorId', filters.vendorId);
    if (filters.jobId) params.append('jobId', filters.jobId);
    if (filters.status) params.append('status', filters.status);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    return await apiHelpers.get(`/bills?${params.toString()}`);
  },

  async getBill(id: string): Promise<Bill> {
    return await apiHelpers.get(`/bills/${id}`);
  },

  async createBill(data: CreateBillData): Promise<Bill> {
    return await apiHelpers.post('/bills', data);
  },

  // Force recompilation v3 - MUST see 🔧 debug logs 
  async createBillWithAttachments(data: CreateBillData, files: FileList | null): Promise<Bill> {
    try {
      console.log('💾 ENTRY: createBillWithAttachments function called');
      console.log('💾 createBillWithAttachments called with data:', data);
      console.log('💾 Files to upload:', files ? Array.from(files).map(f => f.name) : 'No files');
      
      const formData = new FormData();
      
      // Append bill data as JSON string
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          console.log(`💾 Appending ${key}:`, value);
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      });
      
      // Append files
      if (files) {
        Array.from(files).forEach((file) => {
          console.log('💾 Appending file:', file.name);
          formData.append('attachments', file);
        });
      }

      // Use fetch directly for FormData uploads
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/bills`;
      console.log('💾 Making POST request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log('💾 Response status:', response.status);
      console.log('💾 Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Network error' }));
        console.error('💾 API Error:', errorData);
        throw new Error(errorData.message || 'Failed to create bill');
      }

      const result = await response.json();
      console.log('💾 Bill created successfully:', result);
      return result;
    } catch (error) {
      console.error('💾 createBillWithAttachments error:', error);
      throw error;
    }
  },

  async updateBill(id: string, data: UpdateBillData): Promise<Bill> {
    return await apiHelpers.put(`/bills/${id}`, data);
  },

  async updateBillWithAttachments(id: string, data: UpdateBillData, files: FileList | null): Promise<Bill> {
    try {
      console.log('🔧 ENTRY v3: updateBillWithAttachments function called - DEBUGGING ATTACHMENT ISSUE');
      console.log('🔧 updateBillWithAttachments called with id:', id);
      console.log('🔧 updateBillWithAttachments called with data:', data);
      console.log('🔧 Files to upload:', files ? Array.from(files).map(f => f.name) : 'No files');

      if (!files || files.length === 0) {
        console.log('🔧 No files provided, using regular update');
        return await apiHelpers.put(`/bills/${id}`, data);
      }

      console.log('🔧 Files provided, proceeding with attachment upload');
      const formData = new FormData();
      
      // Append bill data as JSON string
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          console.log(`🔧 Appending bill data ${key}:`, value);
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      });
      
      // Append files
      Array.from(files).forEach((file) => {
        console.log('🔧 Appending file:', file.name);
        formData.append('attachments', file);
      });

      // Use fetch directly for FormData uploads
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/bills/${id}/attachments`;
      console.log('🔧 Making POST request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log('🔧 Response status:', response.status);
      console.log('🔧 Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Network error' }));
        console.error('🔧 API Error:', errorData);
        throw new Error(errorData.message || 'Failed to update bill with attachments');
      }

      const result = await response.json();
      console.log('🔧 Bill updated with attachments successfully:', result);
      console.log('🔧 Attachments in result:', result.attachments?.length || 0, result.attachments);
      return result;
    } catch (error) {
      console.error('🔧 updateBillWithAttachments error:', error);
      throw error;
    }
  },


  async getBillStats(): Promise<BillStats> {
    return await apiHelpers.get('/bills/stats');
  },

  async approveBill(id: string): Promise<Bill> {
    return await apiHelpers.put(`/bills/${id}`, { status: 'APPROVED' });
  },

  async deleteAttachment(billId: string, attachmentId: string): Promise<void> {
    return await apiHelpers.delete(`/bills/${billId}/attachments/${attachmentId}`);
  },

  async downloadAttachment(billId: string, attachmentId: string): Promise<Blob> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/bills/${billId}/attachments/${attachmentId}/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download attachment');
    }

    return await response.blob();
  },

  // Helper function to generate unique bill number
  generateBillNumber(sequence?: number): string {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const seq = String(sequence || 1).padStart(4, '0');
    return `BILL${year}${month}${seq}`;
  },

  // Helper function to validate bill data
  validateBillData(data: CreateBillData, isEditing: boolean = false): string[] {
    const errors: string[] = [];

    // Only check bill number for editing, not for creating new bills
    if (isEditing && (!data.billNumber || data.billNumber.trim().length === 0)) {
      errors.push('Bill number is required');
    }

    if (!data.vendorId) {
      errors.push('Vendor is required');
    }

    if (!data.dueDate) {
      errors.push('Due date is required');
    }

    // Check if there are any valid items (after filtering empty ones)
    const validItems = data.items?.filter(item => 
      item.itemId || item.description.trim().length > 0 || item.rate > 0
    ) || [];
    
    console.log('Valid items for "at least one" check:', validItems);
    
    // Only require items if user has filled out the form completely
    // Allow creating bills without items initially (can be added later)
    if (validItems.length === 0) {
      console.log('No valid items - allowing empty bill creation');
      // Don't add error - allow empty bills
    }

    // Validate items (only validate items that have some data entered)
    console.log('Validating items:', data.items);
    data.items?.forEach((item, index) => {
      // Check if this item has any meaningful data (not completely empty)
      const hasData = item.itemId || item.description.trim().length > 0 || item.rate > 0;
      console.log(`Item ${index + 1} hasData:`, hasData, 'item:', item);
      
      if (hasData) {
        // If item has some data, validate all required fields
        if (!item.itemId) {
          errors.push(`Item ${index + 1}: Item selection is required`);
          console.log(`Added error: Item ${index + 1}: Item selection is required`);
        }
        if (!item.description || item.description.trim().length === 0) {
          errors.push(`Item ${index + 1}: Description is required`);
          console.log(`Added error: Item ${index + 1}: Description is required`);
        }
        if (!item.quantity || item.quantity <= 0) {
          errors.push(`Item ${index + 1}: Quantity must be greater than 0`);
          console.log(`Added error: Item ${index + 1}: Quantity must be greater than 0`);
        }
        if (!item.rate || item.rate <= 0) {
          errors.push(`Item ${index + 1}: Rate must be greater than 0`);
          console.log(`Added error: Item ${index + 1}: Rate must be greater than 0`);
        }
      } else {
        console.log(`Item ${index + 1} is empty, skipping validation`);
      }
    });

    return errors;
  },

  // Helper function to calculate bill totals
  calculateBillTotals(items: Array<{ quantity: number; rate: number; taxRate?: number }>) {
    let subtotal = 0;
    let taxAmount = 0;

    items.forEach(item => {
      const itemAmount = item.quantity * item.rate;
      subtotal += itemAmount;
      
      if (item.taxRate) {
        taxAmount += itemAmount * (item.taxRate / 100);
      }
    });

    const total = subtotal + taxAmount;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      taxAmount: Number(taxAmount.toFixed(2)),
      total: Number(total.toFixed(2))
    };
  },

  // Helper function to format bill status display
  formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'DRAFT': 'Draft',
      'PENDING': 'Pending',
      'APPROVED': 'Approved',
      'PAID': 'Paid',
      'OVERDUE': 'Overdue',
      'CANCELLED': 'Cancelled'
    };
    return statusMap[status] || status;
  },

  // Helper function to get status color
  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'APPROVED': 'bg-blue-100 text-blue-800',
      'PAID': 'bg-green-100 text-green-800',
      'OVERDUE': 'bg-red-100 text-red-800',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  },

  // Helper function to check if bill is overdue
  isOverdue(dueDate: string, status: string): boolean {
    if (status === 'PAID' || status === 'CANCELLED') return false;
    return new Date(dueDate) < new Date() && status !== 'PAID';
  },

  // Delete bill
  async deleteBill(id: string): Promise<{ message: string }> {
    return apiHelpers.delete(`/bills/${id}`);
  }
};